import React, { Component } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonItem,
  IonList,
  IonButtons,
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
  IonRadio,
  IonRadioGroup,
  IonInput,
  IonBackButton,
  IonSlides,
  IonSlide,
  withIonLifeCycle,
} from "@ionic/react";
import { RouteComponentProps, withRouter } from "react-router";

import _ from "lodash";

// Images
import onboardingImageDash from "../../assets/Image_OB_v2Dash@3x.png";
import onboardingImageSearching from "../../assets/Image_OB_Searching@3x.png";
import onboardingImageSpeakContent from "../../assets/Image_OB_SpeakContent@3x.png";
import onboardingImageGuidelineUpdates from "../../assets/Image_OB_GuidelineUpdates@3x.png";
import onboardingImagePins from "../../assets/Image_OB_Pins@3x.png";
import onboardingImageParaPassLink from "../../assets/Image_OB_ParaPassLink@3x.png";
import onboardingImageEditDash from "../../assets/Image_OB_EditDash@3x.png";

import onboardingImageDashCFR from "../../assets/Image_OB_v2Dash_CFR@3x.png";
import onboardingImageSearchingCFR from "../../assets/Image_OB_Searching_CFR@3x.png";
import onboardingImageSpeakContentCFR from "../../assets/Image_OB_SpeakContent_CFR@3x.png";
import onboardingImageGuidelineUpdatesCFR from "../../assets/Image_OB_GuidelineUpdates_CFR@3x.png";
import onboardingImagePinsCFR from "../../assets/Image_OB_Pins_CFR@3x.png";
import onboardingImageParaPassLinkCFR from "../../assets/Image_OB_ParaPassLink_CFR@3x.png";
import onboardingImageEditDashCFR from "../../assets/Image_OB_EditDash_CFR@3x.png";

// CSS
import "../onboarding/OnboardingPage.css";
import "../../Shared.css";
import "../../../App.css";

// Controllers
import {
  IScreenProps,
  IUser,
  IGuideline,
  IGuidelineSummary,
  IClinicalNotice,
  ITrustPin,
  IPathway,
  IAlgorithm,
} from "../../Interfaces";
import AppController from "../../../controllers/AppController";
import DataController from "../../controllers/DataController";

// Components

type Props = RouteComponentProps<{ id: string; tab: string }> & {
  screenProps: IScreenProps;
  features: string[];
};
type State = {};

class OnboardingPage extends Component<Props, State> {
  private slidesRef: React.RefObject<HTMLIonSlidesElement>;

  constructor(props: Props) {
    super(props);

    this.slidesRef = React.createRef();

    this.state = {};
  }

  componentDidMount() {
    window.addEventListener("orientationchange", this.orientationChanged.bind(this));
  }

  componentWillUnmount() {
    window.removeEventListener("orientationchange", this.orientationChanged.bind(this));
  }

  orientationChanged() {
    if (this.slidesRef && this.slidesRef.current) {
      this.slidesRef.current.update();
    }
  }

  async continueButtonTapped() {
    if (this.slidesRef && this.slidesRef.current) {
      let isEnd = await this.slidesRef.current.isEnd();
      if (isEnd) {
        this.skipButtonTapped();
      } else {
        this.slidesRef.current.slideNext();
      }
    }
  }

  skipButtonTapped() {
    this.props.screenProps.finishOnboarding();
  }

  render() {
    let onboardingSlideData = [
      {
        title: "Welcome to JRCALC Plus",
        html: `<p>Providing both national and regional up to date guidance for prehospital professionals across the UK ambulance trusts.</p>

        <p>Here's a quick look of key features so you can make the most of the app.</p>`,
        image: onboardingImageDash,
      },
      {
        title: "Improved searching",
        html: `<p>Streamline your searching with clear labelling and filtering from the dashboard search. See whether your search term matches the guideline title or if there's a match within the guideline's content.</p>

        <p>You can also search within a guideline to go directly to the information you need.</p>`,
        image: onboardingImageSearching,
      },
      {
        title: "Accessibility features",
        html: `<p>Change the display of a guideline to best suit your needs. You can adjust the text size, line height and font. More fonts will be coming soon.</p>

        <p>Text-to-speech is available using a simple play and stop feature to read through either the whole guideline or individual sections.</p>`,
        image: onboardingImageSpeakContent,
      },
      {
        title: "Guideline Updates in context",
        html: `<p>View Guideline Updates in context on the relevant guideline.</p>

        <p>Newly updated guidelines will have a red <b>Updated</b> label when on the Guidelines list view.</p>`,
        image: onboardingImageGuidelineUpdates,
      },
      // {
      //   title: 'Trust pins',
      //   html: `<p>Access curated lists of guidelines that your Trust has put together for their members. </p>

      //   <p>Please note that depending on your Trust, the Pins may change from time to time, and that not every Trust uses Trust Pins.</p>`,
      //   image: onboardingImagePins,
      // },
      {
        title: "Sync with ParaPass",
        html: `<p>Practise CPD by testing your knowledge on a guideline.</p>

        <p>If you have a subscription to ParaPass, you can use the link at the bottom of a guideline to jump straight into a quiz on that guideline in ParaPass.</p>
        
        <p>You can turn these links off in your Settings if you prefer.</p>`,
        image: onboardingImageParaPassLink,
      },
      {
        title: "An enhanced Dashboard",
        html: `<p>Edit your Dashboard to your needs. Tap the <b>Edit</b> button on the Dashboard to reorder or hide Dashboard cards. </p>`,
        image: onboardingImageEditDash,
      },
    ];

    if (this.props.screenProps.app === "CFR") {
      onboardingSlideData = [
        {
          title: "Responder Plus has been updated",
          html: `<p>We’ve enhanced the app by making some improvements. Here’s a quick look of what’s changed so you can make the most of the new features.</p>

          <p>Prefer to read about our updates later? You'll be able to find this information in the menu from the Dashboard.</p>`,
          image: onboardingImageDashCFR,
        },
        {
          title: "Improved searching",
          html: `<p>We have streamlined the search process to help you navigate the content more efficiently.</p>

          <p>Clear labelling and the ability to view your search matches against guideline titles and content.</p>
          
          <p>You can also refine your search using Page Searching within a guideline, as well as the Filter feature to direct you quickly to the information you need.</p>`,
          image: onboardingImageSearchingCFR,
        },
        {
          title: "Speak content",
          html: `<p>Did you know that we now have an audio option? You can turn on audio controls via the Accessibility icon in the top right of a guideline (or via Settings in the main menu).</p>

          <p>Just toggle on ‘Show text-to-speech’ and you’ll be able to see a Play button at the top of guidelines to read out the entire guideline.</p>
          
          <p>You can also play sections individually by pressing ‘Play section’.</p>`,
          image: onboardingImageSpeakContentCFR,
        },
        {
          title: "Guideline updates in context",
          html: `<p>Guideline Updates can now be seen in context on the guideline they refer to. Any updated guidelines will have a red <b>Updated</b> label on the guideline card.</p>

          <p>Acknowledged Guideline Updates appear in a section at the bottom of a guideline.</p>`,
          image: onboardingImageGuidelineUpdatesCFR,
        },
        {
          title: "Trust pins",
          html: `<p>A new card has been added to the Dashboard called <b>Trust Pins</b>. This card features a curated list of guidelines that your Trust thinks is relevant to their members. </p>

          <p>Please note that depending on your Trust, the Pins may change from time to time.</p>`,
          image: onboardingImagePinsCFR,
        },
        {
          title: "Linked to ParaPass",
          html: `<p>Wanting to brush up on your knowledge of a guideline or undertake some CPD? Now you can!</p>

          <p>If you have a subscription to ParaPass, you will now see this section at the bottom of the guideline. Tap the ‘Take Quiz’ button and it will take you directly to the accompanying guideline quiz in ParaPass.</p>`,
          image: onboardingImageParaPassLinkCFR,
        },
        {
          title: "An enhanced dashboard",
          html: `<p>Edit your Dashboard to your needs. Tap the <b>Edit</b> button on the Dashboard to reorder or hide Dashboard cards. </p>`,
          image: onboardingImageEditDashCFR,
        },
      ];
    }

    const slideOpts = {
      initialSlide: 0,
      speed: 400,
      autoHeight: true,
      // longSwipes: false,
      allowSlideNext: true,
      // threshold: 200,
    };

    return (
      <IonPage>
        <IonContent fullscreen>
          <IonSlides className="onboardingSlidesContainer" pager={true} options={slideOpts} ref={this.slidesRef}>
            {onboardingSlideData.map((onboardingSlide, i: number) => (
              <IonSlide key={i}>
                <div className="onboardingSlide">
                  <div className="onboardingSlideImageContainer">
                    <img className="onboardingSlideImage" src={onboardingSlide.image} />
                  </div>
                  <div className="onboardingSlideBottomContent">
                    <div className="onboardingSlideTitle">{onboardingSlide.title}</div>
                    <div
                      className="onboardingSlideDescription"
                      key={i}
                      dangerouslySetInnerHTML={{ __html: onboardingSlide.html }}
                    />
                  </div>
                  <IonButton
                    mode="ios"
                    className="onboardingSlideContinueButton filledButton"
                    onClick={this.continueButtonTapped.bind(this)}
                  >
                    Continue
                  </IonButton>
                </div>
              </IonSlide>
            ))}
          </IonSlides>

          <div className="onboardingSkipButton" onClick={this.skipButtonTapped.bind(this)}>
            Skip
          </div>
        </IonContent>
      </IonPage>
    );
  }
}

export default withIonLifeCycle(OnboardingPage);
